import { AllLicenseTypes } from '@shared/models/organizations/AllLicenseTypes';
import { OrganizationModelLicenseKeys } from '@shared/models/organizations/OrganizationModelLicenses';

export const EnableHeadMouseMode = 'enableHeadMouseMode';
export const EnableCephablePanel = 'enableCephablePanel';
export const EnableAutomaticProfileSwitching = 'enableAutomaticProfileSwitching';
export const EnableTextGeneration = 'enableTextGeneration';
export const EnableWizard = 'enableWizard';
export const EnableAiCommands = 'EnableAiCommands';
export const EnableAiDictation = 'enableAiDictation';
export const EnableTranscribeTask = 'enableTranscribeTask';
export const EnableClickByName = 'enableClickByName';
export const EnableGamepadSupport = 'enableGamepadSupport';
export const EnableMetrics = 'enableMetrics';
export const EnableAiWorkflows = 'enableAiWorkflows';

export type AvailableFeatureFlags =
    | typeof EnableHeadMouseMode
    | typeof EnableCephablePanel
    | typeof EnableAutomaticProfileSwitching
    | typeof EnableTextGeneration
    | typeof EnableWizard
    | typeof EnableAiCommands
    | typeof EnableAiDictation
    | typeof EnableClickByName
    | typeof EnableGamepadSupport
    | typeof EnableTranscribeTask
    | typeof EnableMetrics
    | typeof EnableAiWorkflows;

// Beta Feature Flags by User Type
export const BetaLicenseFeatureFlags: Record<AllLicenseTypes, string[]> = {
    personal: [EnableGamepadSupport],
    isPlusAccess: [EnableClickByName, EnableTranscribeTask, EnableGamepadSupport],
    isEmployeeAccess: [EnableTranscribeTask, EnableMetrics, EnableClickByName],
    isStudentAccess: [EnableMetrics, EnableClickByName],
    isDeveloperAccess: [EnableMetrics, EnableClickByName],
};

export const BetaAllFeatureFlags: string[] = [...new Set(Object.values(BetaLicenseFeatureFlags).flat())]; // All unique beta feature flags for all license types

// These are the feature flags that are enabled by default for each license type
// TODO See https://github.com/Cephable/Cephable-Web-Clients/issues/2024 in dataLoader/index.ts
export const PersonalLicenseEnabledFeatureFlags: string[] = [
    EnableAutomaticProfileSwitching,
    EnableCephablePanel,
    EnableHeadMouseMode,
];

export const licensePriorityOrder: AllLicenseTypes[] = [
    'isPlusAccess',
    'isEmployeeAccess',
    'isDeveloperAccess',
    'isStudentAccess',
];

const licenseTypesInHighestPriorityOrder: AllLicenseTypes[] = [
    'isEmployeeAccess',
    'isDeveloperAccess',
    'isStudentAccess',
    'isPlusAccess',
    'personal',
];

export const LicenseEnabledFeatureFlags: Record<OrganizationModelLicenseKeys, string[]> = {
    isEmployeeAccess: [EnableTextGeneration, EnableWizard, EnableAiCommands, EnableAiDictation, EnableAiWorkflows],
    isDeveloperAccess: [EnableWizard, EnableAiCommands, EnableAiDictation],
    isStudentAccess: [EnableWizard, EnableAiCommands, EnableAiDictation],
    isPlusAccess: [EnableTextGeneration, EnableWizard, EnableAiCommands, EnableAiDictation],
};

export const getMinimumLicenseType = (flag: string): AllLicenseTypes | undefined => {
    if (PersonalLicenseEnabledFeatureFlags.includes(flag)) {
        return 'personal';
    }
    for (const licenseType of licensePriorityOrder) {
        const flags = LicenseEnabledFeatureFlags[licenseType as OrganizationModelLicenseKeys];
        if (flags && flags.includes(flag)) {
            return licenseType;
        }
    }
    for (const licenseType of Object.keys(BetaLicenseFeatureFlags) as AllLicenseTypes[]) {
        const flags = BetaLicenseFeatureFlags[licenseType];
        if (flags && flags.includes(flag)) {
            return licenseType;
        }
    }
    return;
};

export const getMinimumLicenseTypeForFeatureFlags = (flags: string[]): AllLicenseTypes | undefined => {
    if (!flags || flags.length === 0) return;
    const licenseTypesFound = new Set<AllLicenseTypes>();
    for (const flag of flags) {
        const licenseType = getMinimumLicenseType(flag);
        if (licenseType) {
            licenseTypesFound.add(licenseType);
        }
    }
    if (licenseTypesFound.size === 0) return; // No valid license types found
    for (const licenseType of licenseTypesInHighestPriorityOrder) {
        if (licenseTypesFound.has(licenseType)) {
            return licenseType;
        }
    }
    return;
};
